'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ScientificNotationCalculator() {
  const t = useTranslations("tools.scientificNotationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Converter state
  const [convertInput, setConvertInput] = useState('');
  const [converterResults, setConverterResults] = useState<any>(null);

  // Calculator state
  const [xCoefficient, setXCoefficient] = useState('1.23');
  const [xExponent, setXExponent] = useState('7');
  const [yCoefficient, setYCoefficient] = useState('3.45');
  const [yExponent, setYExponent] = useState('2');
  const [precision, setPrecision] = useState('20');
  const [calculatorResults, setCalculatorResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Utility functions
  const parseInput = (input: string): number => {
    input = input.trim().replace(/\s+/g, '');

    if (input.match(/^-?\d*\.?\d+[eE][+-]?\d+$/)) {
      return parseFloat(input);
    }

    const xMatch = input.match(/^(-?\d*\.?\d+)[xX×*]10\^?([+-]?\d+)$/);
    if (xMatch) {
      return parseFloat(xMatch[1]) * Math.pow(10, parseFloat(xMatch[2]));
    }

    return parseFloat(input);
  };

  const toScientific = (num: number, precision: number = 20) => {
    if (num === 0) return { coefficient: 0, exponent: 0 };

    const absNum = Math.abs(num);
    const exponent = Math.floor(Math.log10(absNum));
    const coefficient = num / Math.pow(10, exponent);

    return {
      coefficient: parseFloat(coefficient.toFixed(precision)),
      exponent: exponent
    };
  };

  const toEngineering = (num: number, precision: number = 20) => {
    if (num === 0) return { coefficient: 0, exponent: 0 };

    const absNum = Math.abs(num);
    const exponent = Math.floor(Math.log10(absNum));
    const engExponent = Math.floor(exponent / 3) * 3;
    const coefficient = num / Math.pow(10, engExponent);

    return {
      coefficient: parseFloat(coefficient.toFixed(precision)),
      exponent: engExponent
    };
  };

  const formatScientific = (sci: { coefficient: number; exponent: number }) => {
    if (sci.coefficient === 0) return '0';
    return `${sci.coefficient} × 10^${sci.exponent}`;
  };

  const formatENotation = (sci: { coefficient: number; exponent: number }) => {
    if (sci.coefficient === 0) return '0';
    return `${sci.coefficient}e${sci.exponent}`;
  };

  const formatRealNumber = (num: number, precision: number = 20) => {
    if (Math.abs(num) < 1e-10 || Math.abs(num) > 1e10) {
      return num.toExponential(precision).replace(/\.?0+e/, 'e');
    }
    return parseFloat(num.toPrecision(precision)).toString();
  };

  // Converter handler
  const handleConvert = (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const num = parseInput(convertInput);

      if (isNaN(num) || !isFinite(num)) {
        alert(t('invalidNumberError'));
        return;
      }

      const sci = toScientific(num);
      const eng = toEngineering(num);

      setConverterResults({
        scientific: formatScientific(sci),
        eNotation: formatENotation(sci),
        engineering: formatScientific(eng),
        realNumber: formatRealNumber(num)
      });
    } catch (error) {
      alert(t('parseError'));
    }
  };

  const clearConverter = () => {
    setConvertInput('');
    setConverterResults(null);
  };

  // Calculator operations
  const handleOperation = (operation: string) => {
    const xCoef = parseFloat(xCoefficient);
    const xExp = parseFloat(xExponent);
    const yCoef = parseFloat(yCoefficient);
    const yExp = parseFloat(yExponent);
    const prec = parseInt(precision);

    if (isNaN(xCoef) || isNaN(xExp)) {
      alert(t('invalidXError'));
      return;
    }

    const x = xCoef * Math.pow(10, xExp);
    let result: number;
    let opDisplay: string;

    switch(operation) {
      case 'add':
        if (isNaN(yCoef) || isNaN(yExp)) {
          alert(t('invalidYError'));
          return;
        }
        const y = yCoef * Math.pow(10, yExp);
        result = x + y;
        opDisplay = `${formatScientific({coefficient: xCoef, exponent: xExp})} + ${formatScientific({coefficient: yCoef, exponent: yExp})}`;
        break;
      case 'subtract':
        if (isNaN(yCoef) || isNaN(yExp)) {
          alert(t('invalidYError'));
          return;
        }
        const y2 = yCoef * Math.pow(10, yExp);
        result = x - y2;
        opDisplay = `${formatScientific({coefficient: xCoef, exponent: xExp})} − ${formatScientific({coefficient: yCoef, exponent: yExp})}`;
        break;
      case 'multiply':
        if (isNaN(yCoef) || isNaN(yExp)) {
          alert(t('invalidYError'));
          return;
        }
        const y3 = yCoef * Math.pow(10, yExp);
        result = x * y3;
        opDisplay = `${formatScientific({coefficient: xCoef, exponent: xExp})} × ${formatScientific({coefficient: yCoef, exponent: yExp})}`;
        break;
      case 'divide':
        if (isNaN(yCoef) || isNaN(yExp)) {
          alert(t('invalidYError'));
          return;
        }
        const y4 = yCoef * Math.pow(10, yExp);
        if (y4 === 0) {
          alert(t('divideByZeroError'));
          return;
        }
        result = x / y4;
        opDisplay = `${formatScientific({coefficient: xCoef, exponent: xExp})} ÷ ${formatScientific({coefficient: yCoef, exponent: yExp})}`;
        break;
      case 'power':
        if (isNaN(yCoef) || isNaN(yExp)) {
          alert(t('invalidYError'));
          return;
        }
        const y5 = yCoef * Math.pow(10, yExp);
        result = Math.pow(x, y5);
        opDisplay = `${formatScientific({coefficient: xCoef, exponent: xExp})}^${formatScientific({coefficient: yCoef, exponent: yExp})}`;
        break;
      case 'sqrt':
        if (x < 0) {
          alert(t('sqrtNegativeError'));
          return;
        }
        result = Math.sqrt(x);
        opDisplay = `√${formatScientific({coefficient: xCoef, exponent: xExp})}`;
        break;
      case 'square':
        result = x * x;
        opDisplay = `${formatScientific({coefficient: xCoef, exponent: xExp})}²`;
        break;
      default:
        return;
    }

    if (!isFinite(result)) {
      alert(t('invalidResultError'));
      return;
    }

    const resultSci = toScientific(result, prec);

    setCalculatorResults({
      operation: opDisplay,
      scientific: formatScientific(resultSci),
      eNotation: formatENotation(resultSci),
      realNumber: formatRealNumber(result, prec)
    });
  };

  const clearCalculator = () => {
    setXCoefficient('1.23');
    setXExponent('7');
    setYCoefficient('3.45');
    setYExponent('2');
    setPrecision('20');
    setCalculatorResults(null);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: '#1e40af', marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const converterSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const sectionTitleStyle = getResponsiveStyle(
    { color: '#2563eb', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const sectionDescriptionStyle = getResponsiveStyle(
    { color: '#64748b', marginBottom: '1.5rem', lineHeight: 1.6 },
    { fontSize: '0.875rem' },
    { fontSize: '0.9375rem' },
    { fontSize: '1rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', color: '#334155', fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9375rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const scientificInputStyle = getResponsiveStyle(
    { display: 'grid', gap: '0.5rem', alignItems: 'center' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr auto 1fr' },
    { gridTemplateColumns: '1fr auto 1fr' }
  );

  const timestenStyle = getResponsiveStyle(
    { color: '#334155', fontWeight: 600, whiteSpace: 'nowrap' as const },
    { textAlign: 'center' as const, fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', flexWrap: 'wrap' as const, justifyContent: 'center', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      minWidth: '120px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem', flex: 1 },
    { padding: '0.75rem 1.5rem', fontSize: '1rem', flex: 1 }
  );

  const operationBtnStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      background: '#f1f5f9',
      color: '#334155',
      minWidth: '80px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const clearBtnStyle = {
    ...calculateBtnStyle,
    background: '#fee2e2',
    color: '#dc2626'
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      marginTop: '2rem',
      borderLeft: '4px solid #22c55e'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#15803d', marginBottom: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.1875rem' },
    { fontSize: '1.25rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem',
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem'
    },
    { flexDirection: 'column' as const, gap: '0.5rem', alignItems: 'flex-start' },
    { flexDirection: 'row' as const, gap: '0', alignItems: 'center' },
    { flexDirection: 'row' as const, gap: '0', alignItems: 'center' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { color: '#64748b', fontWeight: 600 },
    { fontSize: '0.875rem' },
    { fontSize: '0.9375rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: '#1e40af', fontWeight: 700, fontFamily: "'Courier New', monospace" },
    { fontSize: '1rem', wordBreak: 'break-all' as const },
    { fontSize: '1.0625rem' },
    { fontSize: '1.1rem' }
  );

  const precisionControlStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { flexDirection: 'column' as const, alignItems: 'flex-start' },
    { flexDirection: 'row' as const, alignItems: 'center' },
    { flexDirection: 'row' as const, alignItems: 'center' }
  );

  const precisionInputStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      padding: '0.5rem',
      fontSize: '1rem'
    },
    { width: '100%' },
    { width: '80px' },
    { width: '80px' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2GlassStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolMainTitle')}</h2>
          </div>

          <div style={converterSectionStyle}>
            <h3 style={sectionTitleStyle}>
              <span>📊</span>
              <span>{t('converterTitle')}</span>
            </h3>
            <p style={sectionDescriptionStyle}>
              {t('converterDescription')}
            </p>

            <form onSubmit={handleConvert}>
              <div style={formGroupStyle}>
                <label htmlFor="convert-input" style={labelStyle}>{t('converterInputLabel')}</label>
                <input
                  type="text"
                  id="convert-input"
                  placeholder={t('converterInputPlaceholder')}
                  value={convertInput}
                  onChange={(e) => setConvertInput(e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('convertButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={clearConverter}>{t('clearButton')}</button>
              </div>
            </form>

            {converterResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>{t('conversionResults')}</h3>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('scientificNotation')}</span>
                  <span style={resultValueStyle}>{converterResults.scientific}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('eNotation')}</span>
                  <span style={resultValueStyle}>{converterResults.eNotation}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('engineeringNotation')}</span>
                  <span style={resultValueStyle}>{converterResults.engineering}</span>
                </div>
                <div style={{...resultItemStyle, marginBottom: 0}}>
                  <span style={resultLabelStyle}>{t('realNumber')}</span>
                  <span style={resultValueStyle}>{converterResults.realNumber}</span>
                </div>
              </div>
            )}
          </div>

          <div style={converterSectionStyle}>
            <h3 style={sectionTitleStyle}>
              <span>🔢</span>
              <span>{t('calculatorTitle')}</span>
            </h3>
            <p style={sectionDescriptionStyle}>
              {t('calculatorDescription')}
            </p>

            <div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('xLabel')}</label>
                <div style={scientificInputStyle}>
                  <input
                    type="number"
                    step="any"
                    placeholder={t('coefficientPlaceholder')}
                    value={xCoefficient}
                    onChange={(e) => setXCoefficient(e.target.value)}
                    style={inputStyle}
                    required
                  />
                  <span style={timestenStyle}>× 10</span>
                  <input
                    type="number"
                    placeholder={t('exponentPlaceholder')}
                    value={xExponent}
                    onChange={(e) => setXExponent(e.target.value)}
                    style={inputStyle}
                    required
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('yLabel')}</label>
                <div style={scientificInputStyle}>
                  <input
                    type="number"
                    step="any"
                    placeholder={t('coefficientPlaceholder')}
                    value={yCoefficient}
                    onChange={(e) => setYCoefficient(e.target.value)}
                    style={inputStyle}
                    required
                  />
                  <span style={timestenStyle}>× 10</span>
                  <input
                    type="number"
                    placeholder={t('exponentPlaceholder')}
                    value={yExponent}
                    onChange={(e) => setYExponent(e.target.value)}
                    style={inputStyle}
                    required
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('precisionLabel')}</label>
                <div style={precisionControlStyle}>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={precision}
                    onChange={(e) => setPrecision(e.target.value)}
                    style={precisionInputStyle}
                  />
                  <span style={{color: '#64748b', fontSize: screenSize === 'mobile' ? '0.875rem' : '1rem'}}>{t('digitsAfterDecimal')}</span>
                </div>
              </div>

              <div style={buttonGroupStyle}>
                <button type="button" style={operationBtnStyle} onClick={() => handleOperation('add')}>{t('addButton')}</button>
                <button type="button" style={operationBtnStyle} onClick={() => handleOperation('subtract')}>{t('subtractButton')}</button>
                <button type="button" style={operationBtnStyle} onClick={() => handleOperation('multiply')}>{t('multiplyButton')}</button>
                <button type="button" style={operationBtnStyle} onClick={() => handleOperation('divide')}>{t('divideButton')}</button>
                <button type="button" style={operationBtnStyle} onClick={() => handleOperation('power')}>{t('powerButton')}</button>
                <button type="button" style={operationBtnStyle} onClick={() => handleOperation('sqrt')}>{t('sqrtButton')}</button>
                <button type="button" style={operationBtnStyle} onClick={() => handleOperation('square')}>{t('squareButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={clearCalculator}>{t('clearButton')}</button>
              </div>
            </div>

            {calculatorResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>{t('calculationResult')}</h3>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('operation')}</span>
                  <span style={resultValueStyle}>{calculatorResults.operation}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('scientificNotation')}</span>
                  <span style={resultValueStyle}>{calculatorResults.scientific}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('eNotation')}</span>
                  <span style={resultValueStyle}>{calculatorResults.eNotation}</span>
                </div>
                <div style={{...resultItemStyle, marginBottom: 0}}>
                  <span style={resultLabelStyle}>{t('realNumber')}</span>
                  <span style={resultValueStyle}>{calculatorResults.realNumber}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep1')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep2')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep3')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep4')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t('detailsTitle')}</h2>
        <h3 style={{...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>

        <h3 style={{...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>

        <h3 style={{...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>

        <h3 style={{...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsSubtitle4')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t('faqTitle')}</h2>

        <h3 style={{...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginTop: '1.5rem'}}>{t('faqQuestion1')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer1')}</p>

        <h3 style={{...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginTop: '1.5rem'}}>{t('faqQuestion2')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer2')}</p>

        <h3 style={{...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginTop: '1.5rem'}}>{t('faqQuestion3')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer3')}</p>

        <h3 style={{...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginTop: '1.5rem'}}>{t('faqQuestion4')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer4')}</p>

        <h3 style={{...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginTop: '1.5rem'}}>{t('faqQuestion5')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer5')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://en.wikipedia.org/wiki/Scientific_notation" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference1')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://byjus.com/maths/scientific-notation/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference2')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.open.edu/openlearn/science-maths-technology/mathematics-statistics/using-scientific-calculator/content-section-5" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference3')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://mlpp.pressbooks.pub/mathreviewforchemistry/chapter/scientific-notation/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference4')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.chem.tamu.edu/class/fyp/mathrev/mr-scnot.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}